import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Badge,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import DataTable from "react-data-table-component";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CloudRestroUsers from "../../../services/DatabaseService/CloudRestroUsers";
import DeviceFilterCard from "../component/DeviceFilterCard";

// Custom hook for fetching device list
const useDeviceList = (filters) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDevices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await CloudRestroUsers.getWithDevices(filters);
      setData(Array.isArray(response) ? response : []);
      setError(null);
    } catch (err) {
      setError(err.message);
      toast.error("Failed to fetch device list");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  return { data, loading, error, refetch: fetchDevices };
};

// Status badge component
const StatusBadge = ({ status }) => {
  const statusMap = {
    active: "success",
    inactive: "danger",
    offline: "warning",
    pending: "info",
  };
  return (
    <Badge color={statusMap[status?.toLowerCase()] || "secondary"}>
      {status || "Unknown"}
    </Badge>
  );
};

// Format datetime
const formatDateTime = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

// Expanded devices table
const ExpandedDevices = ({ data, onRefresh }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [logDurationModal, setLogDurationModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [newLogDuration, setNewLogDuration] = useState("");
  const [updating, setUpdating] = useState(false);
  const [showHeartBeatWindow, setShowHeartBeatWindow] = useState(false);
  console.log("🚀 ~ ExpandedDevices ~ showHeartBeatWindow:", showHeartBeatWindow)

  if (!data.devices || data.devices.length === 0) {
    return <div className="p-3">No devices available</div>;
  }

  const handleUpdateLogDuration = (device) => {
    setSelectedDevice(device);
    setNewLogDuration(device.logDurationInMinutes || "");
    setShowHeartBeatWindow(device.showHeartBeatWindow ?? false);
    setLogDurationModal(true);
  };

  const toggleLogDurationModal = () => {
    setLogDurationModal(!logDurationModal);
    if (logDurationModal) {
      setSelectedDevice(null);
      setNewLogDuration("");
      setShowHeartBeatWindow(false);
    }
  };

  const submitLogDurationUpdate = async () => {
    // Validate log duration if it's being changed
    if (newLogDuration && (isNaN(newLogDuration) || parseInt(newLogDuration) <= 0)) {
      toast.error("Please enter a valid log duration (positive number)");
      return;
    }

    // Check if at least one field is being updated
    const hasLogDurationChanged = newLogDuration && parseInt(newLogDuration) !== selectedDevice.logDurationInMinutes;
    const hasHeartbeatChanged = showHeartBeatWindow !== selectedDevice.showHeartBeatWindow;

    if (!hasLogDurationChanged && !hasHeartbeatChanged) {
      toast.error("No changes detected");
      return;
    }

    try {
      setUpdating(true);
      
      // Update log duration if changed
      if (hasLogDurationChanged) {
        await CloudRestroUsers.updateLogDuration(
          selectedDevice.macId,
          newLogDuration,
        );
      }
      
      // Update heartbeat window if changed
      if (hasHeartbeatChanged) {
        await CloudRestroUsers.updateHeartbeatWindow(
          selectedDevice.macId,
          showHeartBeatWindow,
        );
      }
      
      toast.success("Device settings updated successfully");
      toggleLogDurationModal();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error("Failed to update device settings");
    } finally {
      setUpdating(false);
    }
  };

  const tableCustomStyles = {
    headCells: {
      style: {
        paddingLeft: "8px",
        paddingRight: "8px",
        backgroundColor: "#f3f6f9",
        color: "#495057",
        fontSize: "13px",
        fontWeight: "600",
      },
    },
    cells: {
      style: { paddingLeft: "8px", paddingRight: "8px", fontSize: "13px" },
    },
  };

  const deviceColumns = [
    {
      name: "S.N",
      selector: (row, index) => (currentPage - 1) * pageSize + index + 1,
      sortable: true,
      width: "80px",
    },
    {
      name: "Device Name",
      selector: (row) => row.deviceName || "N/A",
      sortable: true,
    },
    { name: "MAC ID", selector: (row) => row.macId, sortable: true },
    {
      name: "Device Code",
      selector: (row) => row.deviceCode || "N/A",
      sortable: true,
    },
    {
      name: "Location",
      selector: (row) => row.location || "N/A",
      sortable: true,
    },
    {
      name: "Terminal",
      selector: (row) => row.terminal || "N/A",
      sortable: true,
    },
    {
      name: "Status",
      cell: (row) => (
        <Badge color={row.status === 1 ? "success" : "danger"}>
          {row.status === 1 ? "Active" : "Inactive"}
        </Badge>
      ),
      center: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <Button
          size="sm"
          color="primary"
          outline
          onClick={() => handleUpdateLogDuration(row)}
        >
          <i className="ri-edit-2-line"></i>
        </Button>
      ),
      center: true,
      width: "140px",
    },
  ];

  return (
    <>
      <DataTable
        columns={deviceColumns}
        data={data.devices}
        pagination
        paginationPerPage={pageSize}
        paginationRowsPerPageOptions={[5, 10, 15, 20]}
        onChangePage={(page) => setCurrentPage(page)}
        onChangeRowsPerPage={(newSize) => setPageSize(newSize)}
        customStyles={tableCustomStyles}
        noDataComponent="No devices available"
        responsive
        striped
      />

      {/* Device Settings Modal */}
      <Modal isOpen={logDurationModal} toggle={toggleLogDurationModal}>
        <ModalHeader toggle={toggleLogDurationModal}>
          Update Device Settings - {selectedDevice?.deviceName || selectedDevice?.macId}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <label className="form-label">Device Name</label>
            <input
              type="text"
              className="form-control"
              value={selectedDevice?.deviceName || ""}
              disabled
            />
          </div>
          <div className="mb-3">
            <label className="form-label">
              Log Duration (Minutes) 
              <span className="text-muted ms-2">
                {selectedDevice?.logDurationInMinutes ? `(Current: ${selectedDevice.logDurationInMinutes} min)` : ''}
              </span>
            </label>
            <input
              type="number"
              className="form-control"
              placeholder="Enter log duration in minutes"
              value={newLogDuration}
              onChange={(e) => setNewLogDuration(e.target.value)}
              min="1"
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Heartbeat Window</label>
            <div className="form-check form-switch">
              <input
                type="checkbox"
                className="form-check-input"
                id="heartbeatWindowSwitch"
                checked={showHeartBeatWindow}
                onChange={(e) => setShowHeartBeatWindow(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="heartbeatWindowSwitch">
                {showHeartBeatWindow ? "Enabled" : "Disabled"}
              </label>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={toggleLogDurationModal}
            disabled={updating}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={submitLogDurationUpdate}
            disabled={updating}
          >
            {updating ? "Updating Settings..." : "Update Settings"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

const DeviceList = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    searchText: "",
    orgCode: "",
    location: "",
    macId: "",
    onlyWithDevices: true,
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const { data, loading, error, refetch } = useDeviceList(debouncedFilters);

  const [selectedOrg, setSelectedOrg] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const toggleModal = () => setModalOpen(!modalOpen);

  const handleOrgClick = (row) => {
    setSelectedOrg(row);
    setModalOpen(true);
  };

  // Handler to refresh device data (selectedOrg will be updated by useEffect)
  const handleRefreshDevices = async () => {
    await refetch();
  };

  // Debounce filters
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters]);

  // Update selectedOrg when data changes
  useEffect(() => {
    if (selectedOrg && data.length > 0) {
      const updatedOrg = data.find(org => org.organizationId === selectedOrg.organizationId);
      if (updatedOrg) {
        setSelectedOrg(updatedOrg);
      }
    }
  }, [data]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      searchText: "",
      orgCode: "",
      location: "",
      macId: "",
      onlyWithDevices: false,
    });
    setCurrentPage(1);
  };

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (currentPage - 1) * pageSize + index + 1,
      sortable: true,
      width: "80px",
    },
    {
      name: "Organization Name",
      cell: (row) => (
        <span
          style={{ cursor: "pointer", color: "#4c6ef5", fontWeight: 500 }}
          onClick={() => handleOrgClick(row)}
        >
          {row.name}
        </span>
      ),
      sortable: true,
    },
    { name: "Org Code", selector: (row) => row.orgCode, sortable: true },
    {
      name: "Status",
      selector: (row) => (
        <StatusBadge status={row.isActive ? "Active" : "Inactive"} />
      ),
      center: true,
    },
  ];

  const tableCustomStyles = {
    headCells: {
      style: {
        paddingLeft: "8px",
        paddingRight: "8px",
        backgroundColor: "#f3f6f9",
        color: "#495057",
        fontSize: "13px",
        fontWeight: "600",
      },
    },
    cells: {
      style: { paddingLeft: "8px", paddingRight: "8px", fontSize: "13px" },
    },
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Organization List" pageTitle="Device Management" />
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "400px" }}
          >
            <Triangle
              height="80"
              width="80"
              color="#4c6ef5"
              ariaLabel="triangle-loading"
              visible={true}
            />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Organization List" pageTitle="Device Management" />

        <DeviceFilterCard
          filters={filters}
          handleFilterChange={handleFilterChange}
          handleClearFilters={handleClearFilters}
          setCurrentPage={setCurrentPage}
          onAddNewClick={() => navigate("/device/new")}
        />

        <Row>
          <Col lg="12">
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Devices</h4>
              </CardHeader>
              <CardBody>
                {error && (
                  <div className="alert alert-danger mb-3">Error: {error}</div>
                )}

                {data.length === 0 ? (
                  <div className="alert alert-info">No devices found</div>
                ) : (
                  <DataTable
                    columns={columns}
                    data={data}
                    pagination
                    paginationPerPage={pageSize}
                    paginationRowsPerPageOptions={[5, 10, 15, 20]}
                    onChangePage={setCurrentPage}
                    onChangeRowsPerPage={(size) => {
                      setPageSize(size);
                      setCurrentPage(1);
                    }}
                    striped
                    customStyles={tableCustomStyles}
                    responsive
                    noDataComponent="No devices available"
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Modal isOpen={modalOpen} toggle={toggleModal} size="xl" centered>
          <ModalHeader toggle={toggleModal}>
            {selectedOrg?.name} - Devices
          </ModalHeader>
          <ModalBody>
            {selectedOrg?.devices?.length > 0 ? (
              <ExpandedDevices data={selectedOrg} onRefresh={handleRefreshDevices} />
            ) : (
              <div className="text-center p-3">
                No devices available for this organization
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default DeviceList;
